import { useState, useEffect } from 'react';
import { adminGetHeroSettings, adminUpdateHeroSettings } from '@/lib/api';
import { FileOrUrlInput } from './MediaInputs';
import { inp } from './styles';

const SAVED_BANNER_MS = 3000;

export function HeroSettingsPanel() {
  const [videoUrl, setVideoUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [heading, setHeading] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [ctaPrimaryText, setCtaPrimaryText] = useState('');
  const [ctaPrimaryLink, setCtaPrimaryLink] = useState('');
  const [ctaSecondaryText, setCtaSecondaryText] = useState('');
  const [ctaSecondaryLink, setCtaSecondaryLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminGetHeroSettings().then(data => {
      setVideoUrl(data.video_url || '');
      setPosterUrl(data.poster_url || '');
      setHeading(data.heading || '');
      setSubtitle(data.subtitle || '');
      setCtaPrimaryText(data.cta_primary_text || '');
      setCtaPrimaryLink(data.cta_primary_link || '');
      setCtaSecondaryText(data.cta_secondary_text || '');
      setCtaSecondaryLink(data.cta_secondary_link || '');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    setSaved(false);
    try {
      await adminUpdateHeroSettings({
        video_url: videoUrl, poster_url: posterUrl,
        heading, subtitle,
        cta_primary_text: ctaPrimaryText, cta_primary_link: ctaPrimaryLink,
        cta_secondary_text: ctaSecondaryText, cta_secondary_link: ctaSecondaryLink,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), SAVED_BANNER_MS);
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-slate-400 text-sm">Loading hero settings...</p>;

  const displayHeading = heading || "Apex's Multi-Sport Indoor Home";
  const displaySubtitle = subtitle || "Parties & events · Batting tunnels · Tournaments · Facility rentals";

  return (
    <div data-testid="hero-settings-panel" className="space-y-6">
      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-1">Background Media</h3>
        <p className="text-slate-400 text-sm mb-6">Upload a file or paste a URL. Leave empty to use defaults.</p>

        {err && <p className="text-red-400 text-sm mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{err}</p>}
        {saved && <p className="text-green-400 text-sm mb-4 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2">Hero settings saved! Changes are live.</p>}

        <div className="space-y-5">
          <FileOrUrlInput label="Background Video (MP4)" hint="Upload MP4 (max 100MB) or paste URL. Recommended: 1920x1080+."
            accept="video/mp4,video/webm,video/quicktime" value={videoUrl} onChange={setVideoUrl} uploadType="video" testId="hero-video" />
          <FileOrUrlInput label="Poster Image (fallback)" hint="Upload JPG/PNG/WebP (max 10MB) or paste URL."
            accept="image/jpeg,image/png,image/webp,image/gif" value={posterUrl} onChange={setPosterUrl} uploadType="image" testId="hero-poster" />
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-1">Hero Content</h3>
        <p className="text-slate-400 text-sm mb-6">Edit the heading, subtitle, and call-to-action buttons. Leave empty to use defaults.</p>

        <div className="space-y-4">
          <div>
            <label className="block text-slate-400 text-xs mb-1.5 font-medium">Heading</label>
            <textarea value={heading} onChange={e => setHeading(e.target.value)} placeholder="Apex's Multi-Sport Indoor Home" data-testid="hero-heading" rows={2}
              className={inp + " resize-none"} />
            <p className="text-slate-500 text-xs mt-1">Main headline. Use line breaks for multi-line headings.</p>
          </div>
          <div>
            <label className="block text-slate-400 text-xs mb-1.5 font-medium">Subtitle</label>
            <textarea value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Parties & events · Batting tunnels · Tournaments · Facility rentals" data-testid="hero-subtitle" rows={3}
              className={inp + " resize-none"} />
            <p className="text-slate-500 text-xs mt-1">Appears below the heading. Supports multiple lines.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-xs mb-1.5 font-medium">Primary Button Text</label>
              <input value={ctaPrimaryText} onChange={e => setCtaPrimaryText(e.target.value)} placeholder="Book Now" data-testid="hero-cta-primary-text" className={inp} />
            </div>
            <div>
              <label className="block text-slate-400 text-xs mb-1.5 font-medium">Primary Button Link</label>
              <input value={ctaPrimaryLink} onChange={e => setCtaPrimaryLink(e.target.value)} placeholder="https://propelsports.myesoftplanner.com" data-testid="hero-cta-primary-link" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-xs mb-1.5 font-medium">Secondary Button Text</label>
              <input value={ctaSecondaryText} onChange={e => setCtaSecondaryText(e.target.value)} placeholder="View Facility" data-testid="hero-cta-secondary-text" className={inp} />
            </div>
            <div>
              <label className="block text-slate-400 text-xs mb-1.5 font-medium">Secondary Button Link</label>
              <input value={ctaSecondaryLink} onChange={e => setCtaSecondaryLink(e.target.value)} placeholder="/facility" data-testid="hero-cta-secondary-link" className={inp} />
              <p className="text-slate-500 text-xs mt-1">Use /page for internal links or https://... for external</p>
            </div>
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="hero-save-btn"
        className="bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-8 py-3 rounded-xl font-semibold text-sm transition-all w-full sm:w-auto">
        {saving ? 'Saving...' : 'Save All Hero Settings'}
      </button>

      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Live Preview</h3>
        <div data-testid="hero-preview" className="relative rounded-xl overflow-hidden aspect-video bg-[#0B1D3A]">
          {videoUrl ? (
            <video key={videoUrl} autoPlay muted loop playsInline poster={posterUrl || undefined} className="w-full h-full object-cover">
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : posterUrl ? (
            <img src={posterUrl} alt="Hero poster" className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0 bg-[#0B1D3A]/75" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-white text-xl md:text-3xl font-bold mb-2 whitespace-pre-line">{displayHeading}</p>
              <p className="text-white/60 text-sm mb-4 whitespace-pre-line">{displaySubtitle}</p>
              <div className="flex items-center justify-center gap-3">
                <span className="bg-[#F26522] text-white px-4 py-1.5 rounded text-xs font-semibold">{ctaPrimaryText || 'Book Now'}</span>
                <span className="border border-white/50 text-white px-4 py-1.5 rounded text-xs font-semibold">{ctaSecondaryText || 'View Facility'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
